/**
 * Catálogo de servicios. Alimenta:
 *  - la sección "Nuestros servicios" de la portada,
 *  - las páginas /servicios y /servicios/<slug> (y sus variantes /latam y /us),
 *  - el sitemap y el prerender (scripts/prerender.mjs),
 *  - los datos estructurados (Service + FAQPage).
 *
 * Archivo .mjs sin JSX para que Node pueda importarlo directamente.
 *
 * ── Modelo ──────────────────────────────────────────────────────────────────
 *  markets:  ['RD','LATAM','USA']  mercados en los que se ofrece (ver MARKETS)
 *  featured: true                  aparece destacado en la portada
 *  pricing:  null | { public, model: 'subscription'|'quote'|'project', currency, tiers, setupNote }
 *            tiers[].from puede ser número (en pricing.currency), { DOP, USD } o null (a cotizar)
 *  Listas (deliverables.items, benefits): cada item puede ser
 *            string | (ctx) => string | { text: string|fn, markets?: ['RD',...] }
 *            Se resuelven con resolveList(items, market) → string[]
 *  faqs[].answer: string | (ctx) => string  (resolver con resolveFaqs(service, market))
 *  lang:     'es' (por defecto) | 'en'   idioma principal del contenido de la página
 *  overrides: { LATAM: { seoTitle, metaDescription, heroTitle?, heroSubtitle? }, USA: {…} }
 *            textos que sustituyen a los de RD en las variantes /latam y /us (resolver con serviceForMarket)
 *
 * ── Claims de certificación (LEER) ──────────────────────────────────────────
 *  Coralan Multiservice está certificada como EMISOR ELECTRÓNICO ante la DGII y ha
 *  acompañado a 5 empresas en su certificación. NO somos (aún) PSFE / Proveedor de
 *  Servicios de Facturación Electrónica autorizado. Ningún texto debe insinuarlo.
 *  Frases dudosas llevan el comentario // REVISAR CLAIM.
 */
import { formatPrice, resolveAmount } from '../utils/currency.mjs';

export const MARKETS = {
    RD:    { code: 'RD',    label: 'República Dominicana', locale: 'es-DO', currency: 'DOP', currencySymbol: 'RD$', pathPrefix: ''       },
    LATAM: { code: 'LATAM', label: 'Latinoamérica',        locale: 'es',    currency: 'USD', currencySymbol: 'US$', pathPrefix: '/latam' },
    USA:   { code: 'USA',   label: 'United States',        locale: 'en-US', currency: 'USD', currencySymbol: '$',   pathPrefix: '/us'    },
};

export const DEFAULT_MARKET = 'RD';

/** Texto reutilizable y aprobado sobre la certificación (única redacción permitida). */
export const CERTIFIED_EMITTER_CLAIM =
    'Coralan Multiservice está certificada como Emisor Electrónico ante la DGII y hemos acompañado a 5 empresas en su propia certificación.';

const IMG = '/assets/custom/images';

export const SERVICES = [
    // ───────────────────────────────────────────────────────────────────────
    // 1. Facturación Electrónica (e-CF) — producto principal, sólo mercado RD
    // ───────────────────────────────────────────────────────────────────────
    {
        slug: 'facturacion-electronica-ecf',
        name: 'Facturación electrónica (e-CF)',
        shortName: 'Facturación electrónica',
        icon: 'fables-iconbussiness4',
        image: `${IMG}/facturacion-electronica.jpg`, // TODO: crear imagen (no existe aún)
        markets: ['RD'],
        featured: true,
        lang: 'es',
        seoTitle: 'Facturación electrónica e-CF en República Dominicana: cumpla sin cambiar de sistema',
        metaDescription:
            'Emita comprobantes fiscales electrónicos (e-CF) ante la DGII desde RD$990/mes o integrados por API a su ERP, POS o eCommerce actual. Acompañamiento completo en la certificación como emisor electrónico. Emisor Electrónico certificado ante la DGII.',
        heroTitle: 'Facturación electrónica e-CF: cumpla con la DGII sin cambiar el sistema que ya usa',
        heroSubtitle:
            'Emita e-CF desde nuestro portal o intégrelo por API a su ERP, POS o tienda en línea. Somos desarrolladores antes que vendedores de software: nos adaptamos a lo que su empresa ya tiene.',
        badge: 'certified-emitter',
        productLink: { label: 'Ver el software de facturación + contabilidad', path: '/facturacion-electronica' },
        intro: [
            'La mayoría de los proveedores de facturación electrónica le obligan a migrar toda su operación a su plataforma. Nosotros hacemos lo contrario: conectamos la emisión de comprobantes fiscales electrónicos (e-CF) con el sistema que su empresa ya utiliza —ERP, punto de venta, contabilidad o tienda en línea— y, si no tiene ninguno, le damos un portal web listo para facturar desde el primer día.',
            `${CERTIFIED_EMITTER_CLAIM} Ese recorrido lo conocemos de primera mano: postulación, certificado digital, set de pruebas, declaración jurada y puesta en producción.`,
        ],
        deliverables: {
            title: '¿Qué incluye el servicio de facturación electrónica?',
            items: [
                'Emisión de los 10 tipos de e-CF: 31 (crédito fiscal), 32 (consumo), 33 (nota de débito), 34 (nota de crédito), 41 (compras), 43 (gastos menores), 44 (regímenes especiales), 45 (gubernamental), 46 (exportaciones) y 47 (pagos al exterior).',
                'Integración por API REST con su ERP, POS, sistema contable o eCommerce actual.',
                'Acompañamiento en la postulación ante la DGII, el set de pruebas y la declaración jurada hasta obtener la certificación como emisor electrónico.',
                'Gestión del certificado de firma digital y de las secuencias e-NCF autorizadas.',
                'Portal web para emitir, consultar y anular comprobantes sin instalar nada.',
                'Representación impresa (RI) con código QR y envío automático al cliente por correo o WhatsApp.',
                'Almacenamiento seguro y consulta de los e-CF emitidos y recibidos durante el plazo legal.',
                'Aprobación o rechazo comercial de las facturas electrónicas de sus proveedores.',
            ],
        },
        process: {
            title: 'Cómo lo implementamos',
            steps: [
                { title: 'Diagnóstico', text: 'Revisamos su sistema actual, volumen de comprobantes, tipos de e-CF que necesita y el grupo/fecha que le aplica según la DGII (plazo final: 15 de noviembre de 2026).' },
                { title: 'Certificado digital', text: 'Le orientamos para obtener el certificado de firma digital con una entidad autorizada por INDOTEL y lo configuramos.' },
                { title: 'Integración', text: 'Conectamos la emisión de e-CF con su ERP/POS/eCommerce por API o habilitamos el portal web.' },
                { title: 'Set de pruebas', text: 'Ejecutamos con usted el set de pruebas de la DGII y preparamos la declaración jurada.' },
                { title: 'Producción y soporte', text: 'Salida a producción, capacitación y soporte local en español y horario dominicano.' },
            ],
        },
        technologies: ['API REST', 'XML e-CF DGII', 'Firma digital', 'Node.js', 'PHP / Laravel', 'React', 'PostgreSQL'],
        benefits: [
            'No tiene que reemplazar su sistema actual: integramos la emisión de e-CF donde ya trabaja.',
            'Un solo proveedor para el software, la integración y el acompañamiento en la certificación.',
            'Acompañamiento real en la certificación como emisor electrónico, no un PDF de instrucciones.',
            'Soporte local, en español y en horario dominicano.',
        ],
        pricing: {
            public: true,
            model: 'subscription',
            currency: 'DOP',
            tiers: [
                {
                    name: 'Facturador',
                    from: 990,
                    period: 'mes',
                    includes: 'Emisión de e-CF desde el portal web, todos los tipos de comprobante, envío automático al cliente.',
                },
                {
                    name: 'Facturador + Contabilidad',
                    from: 1990,
                    period: 'mes',
                    includes: 'Todo lo anterior más sistema contable integrado, reportes y conciliación.',
                    note: 'Equivalente aproximado a US$40/mes.',
                },
                {
                    name: 'Integración a la medida',
                    from: null,
                    period: null,
                    includes: 'Conexión por API con su ERP, POS o sistema existente. Se cotiza según el sistema a integrar.',
                },
            ],
            setupNote: 'El acompañamiento en la certificación como emisor electrónico ante la DGII se cotiza aparte.',
        },
        faqs: [
            {
                question: '¿Es obligatoria la facturación electrónica en República Dominicana?',
                answer:
                    'Sí. La Ley 32-23 de Facturación Electrónica establece la obligatoriedad de emitir comprobantes fiscales electrónicos (e-CF) de forma escalonada según el tipo de contribuyente: los grandes y medianos ya están obligados, y el plazo final para micro, pequeñas empresas y contribuyentes no clasificados vence el 15 de noviembre de 2026. Le ayudamos a verificar el grupo y la fecha que le aplican según la información oficial de la DGII.',
            },
            {
                question: '¿Tengo que cambiar mi sistema de facturación o mi ERP?',
                answer:
                    'No. Ese es justamente nuestro enfoque: integramos la emisión de e-CF por API con el sistema que ya usa (ERP, POS, contabilidad o eCommerce). Si hoy no tiene sistema, le damos un portal web listo para facturar.',
            },
            {
                question: '¿Cuánto toma certificarse como emisor electrónico ante la DGII?',
                answer:
                    'Depende del volumen y de qué tan listo esté su sistema, pero típicamente el proceso —certificado digital, integración, set de pruebas y declaración jurada— toma entre 3 y 8 semanas. ' +
                    CERTIFIED_EMITTER_CLAIM,
            },
            {
                question: '¿Qué pasa si la DGII o mi conexión fallan? ¿Puedo seguir facturando?',
                answer:
                    'Sí. La normativa contempla un procedimiento de contingencia: los comprobantes se emiten con la secuencia autorizada, se entregan al cliente y se transmiten a la DGII cuando el servicio se restablece. Nuestro sistema gestiona ese flujo automáticamente; puede leer nuestra Política de Contingencia en el sitio.',
            },
            {
                question: '¿Puedo emitir e-CF desde mi tienda en línea o mi aplicación móvil?',
                answer:
                    'Sí. Mediante la API, cada venta de su eCommerce, app móvil o punto de venta puede generar automáticamente su comprobante fiscal electrónico, firmado y transmitido a la DGII, con envío al cliente por correo o WhatsApp.',
            },
            {
                question: '¿Qué certificación tiene Coralan Multiservice ante la DGII?',
                answer:
                    // REVISAR CLAIM: redacción conservadora a propósito. Actualizar cuando la DGII publique la autorización como PSFE.
                    'Coralan Multiservice está certificada como Emisor Electrónico ante la DGII y ha acompañado a 5 empresas en su propia certificación como emisores. Nuestro software y nuestras integraciones cumplen las especificaciones técnicas de la DGII para la emisión de e-CF.',
            },
        ],
        related: ['desarrollo-software-a-medida', 'ecommerce-tiendas-en-linea', 'desarrollo-aplicaciones-moviles'],
        homeCard: {
            title: 'Facturación electrónica (e-CF)',
            description: 'Emita comprobantes fiscales electrónicos ante la DGII desde RD$990/mes o integrados a su sistema actual.',
        },
    },

    // ───────────────────────────────────────────────────────────────────────
    // 2. Software a la medida
    // ───────────────────────────────────────────────────────────────────────
    {
        slug: 'desarrollo-software-a-medida',
        name: 'Desarrollo de software a la medida',
        shortName: 'Software a la medida',
        icon: 'fables-iconbussiness3',
        image: `${IMG}/software-a-medida.jpg`, // TODO: crear imagen (antes compartía mobile-apps.jpg)
        markets: ['RD', 'LATAM', 'USA'],
        featured: true,
        overrides: {
            LATAM: {
                seoTitle: 'Desarrollo de software a la medida para empresas de Latinoamérica',
                metaDescription:
                    'Desarrollamos software a la medida para empresas de Latinoamérica desde República Dominicana: sistemas empresariales, CRM, ERP, POS, plataformas SaaS, APIs e integraciones. Cotizaciones en USD.',
            },
            USA: {
                seoTitle: 'Custom software development from the Dominican Republic for US companies',
                metaDescription:
                    'Custom business systems, CRM/ERP, POS, SaaS platforms and API integrations built by a nearshore team in Santo Domingo, Dominican Republic, on US Eastern business hours. Content in Spanish; contact us in English.',
            },
        },
        seoTitle: 'Desarrollo de software a la medida',
        metaDescription:
            'Desarrollamos software a la medida en Santo Domingo: sistemas empresariales, CRM, ERP, POS, plataformas SaaS, APIs e integraciones para empresas dominicanas e internacionales.',
        heroTitle: 'Desarrollo de software a la medida de su negocio',
        heroSubtitle:
            'Más de 15 años desarrollando sistemas empresariales, CRM, ERP, POS y plataformas SaaS diseñados alrededor de sus procesos, no al revés.',
        intro: [
            'Cuando un software genérico obliga a su equipo a trabajar con hojas de cálculo paralelas, procesos manuales o "parches", es momento de una solución a la medida. En Coralan Multiservice analizamos cómo opera su empresa y construimos la aplicación que realmente necesita: desde un sistema interno de gestión hasta una plataforma SaaS multi-empresa lista para escalar.',
            'Trabajamos con empresas de Santo Domingo, del resto de República Dominicana y clientes internacionales, con un enfoque en entregas incrementales, código mantenible y soporte continuo después del lanzamiento.',
        ],
        deliverables: {
            title: '¿Qué desarrollamos?',
            items: [
                'Sistemas empresariales de gestión (inventario, compras, ventas, cuentas por cobrar y por pagar).',
                'CRM a la medida para seguimiento comercial, clientes y postventa.',
                { text: 'Puntos de venta (POS) integrados con facturación electrónica e-CF de la DGII.', markets: ['RD'] },
                { text: 'Puntos de venta (POS) integrados con su facturación y contabilidad.', markets: ['LATAM', 'USA'] },
                'Plataformas SaaS multi-tenant con suscripciones, roles y reportes en tiempo real.',
                { text: 'APIs REST y servicios de integración entre sistemas (ERP, pasarelas de pago, mensajería, DGII).', markets: ['RD'] },
                { text: 'APIs REST y servicios de integración entre sistemas (ERP, pasarelas de pago, mensajería, autoridades fiscales).', markets: ['LATAM', 'USA'] },
                'Migración y modernización de sistemas legados (PHP, Excel/Access, escritorio) a la web.',
                'Paneles administrativos, dashboards y reportería con exportación a Excel/PDF.',
            ],
        },
        process: {
            title: 'Cómo trabajamos',
            steps: [
                { title: 'Descubrimiento', text: 'Levantamos requerimientos, mapeamos procesos y definimos alcance, prioridades y presupuesto.' },
                { title: 'Diseño y prototipo', text: 'Diseñamos la arquitectura, la base de datos y prototipos de pantallas para validar antes de programar.' },
                { title: 'Desarrollo iterativo', text: 'Entregas por etapas cada 2–3 semanas para que vea avances reales y ajuste el rumbo a tiempo.' },
                { title: 'Pruebas y despliegue', text: 'Pruebas funcionales, capacitación a usuarios y puesta en producción en la nube (AWS, Google Cloud o su servidor).' },
                { title: 'Soporte y evolución', text: 'Mantenimiento, monitoreo y nuevas funcionalidades bajo un plan de soporte.' },
            ],
        },
        technologies: ['React', 'Node.js', 'PHP / Laravel', 'React Native', 'PostgreSQL', 'MySQL', 'Firebase', 'AWS', 'Google Cloud'],
        benefits: [
            'Software que se adapta a sus procesos y crece con su empresa.',
            'Propiedad total del código y de los datos.',
            { text: 'Integración con lo que ya usa: contabilidad, facturación electrónica, pasarelas de pago.', markets: ['RD'] },
            { text: 'Integración con lo que ya usa: contabilidad, facturación, pasarelas de pago.', markets: ['LATAM', 'USA'] },
            'Un solo proveedor para desarrollo, diseño, hosting y soporte.',
        ],
        pricing: { public: false, model: 'quote', currency: 'DOP', tiers: [], setupNote: '' },
        faqs: [
            {
                question: '¿Cuánto cuesta desarrollar un software a la medida en República Dominicana?',
                answer:
                    'Depende del alcance: un sistema interno sencillo puede iniciar en pocas semanas de trabajo, mientras que una plataforma SaaS completa es un proyecto de varios meses. Tras una reunión de descubrimiento sin costo entregamos una propuesta con alcance, etapas y precio cerrado por fase.',
            },
            {
                question: '¿Cuánto tiempo toma un proyecto de software a la medida?',
                answer:
                    'Proyectos pequeños: 4 a 8 semanas. Sistemas empresariales o SaaS: 3 a 6 meses en entregas incrementales. Desde la primera etapa usted recibe módulos funcionales, no tiene que esperar al final para ver resultados.',
            },
            {
                question: '¿Pueden integrarse con la DGII para facturación electrónica?',
                answer:
                    'Sí. Coralan Multiservice está certificada como Emisor Electrónico ante la DGII y hemos acompañado a 5 empresas en su propia certificación. Podemos integrar la emisión de comprobantes fiscales electrónicos en su sistema a la medida, POS o eCommerce.',
                markets: ['RD'],
            },
            {
                question: '¿El código y los datos son míos?',
                answer:
                    'Sí. Al finalizar cada fase usted es dueño del código fuente y de la base de datos. Le entregamos documentación y, si lo desea, desplegamos en su propia cuenta de nube.',
            },
        ],
        related: ['facturacion-electronica-ecf', 'desarrollo-web', 'desarrollo-aplicaciones-moviles', 'automatizaciones-inteligencia-artificial'],
        homeCard: {
            title: 'Software a la medida',
            description: 'Sistemas empresariales, CRM, ERP, POS y plataformas SaaS diseñados alrededor de los procesos de su negocio.',
        },
    },

    // ───────────────────────────────────────────────────────────────────────
    // 3. Desarrollo web
    // ───────────────────────────────────────────────────────────────────────
    {
        slug: 'desarrollo-web',
        name: 'Desarrollo web',
        shortName: 'Desarrollo web',
        icon: 'fables-icondevelopment-icon',
        image: `${IMG}/laptop-1209008.jpg`,
        markets: ['RD', 'LATAM', 'USA'],
        featured: true,
        overrides: {
            LATAM: {
                seoTitle: 'Desarrollo web para empresas de Latinoamérica: Drupal, WordPress y React',
                metaDescription:
                    'Diseño y desarrollo de sitios web corporativos, portales y aplicaciones web con Drupal, WordPress, React y Next.js para empresas de toda Latinoamérica. Rápidos, seguros y optimizados para SEO. Cotizaciones en USD.',
            },
            USA: {
                seoTitle: 'Web development for US companies: Drupal, WordPress and React (nearshore)',
                metaDescription:
                    'Corporate websites, portals and web applications built with Drupal, WordPress, React and Next.js by a nearshore team in the Dominican Republic on US Eastern hours. Content in Spanish; contact us in English.',
            },
        },
        seoTitle: 'Desarrollo web en Santo Domingo: Drupal, WordPress y React',
        metaDescription:
            'Diseño y desarrollo de sitios web corporativos, portales y aplicaciones web en República Dominicana con Drupal, WordPress, React y Next.js. Rápidos, seguros y optimizados para SEO.',
        heroTitle: 'Desarrollo web profesional para empresas',
        heroSubtitle:
            'Sitios corporativos, portales institucionales y aplicaciones web con Drupal, WordPress o JavaScript moderno.',
        intro: [
            'Su sitio web es la primera impresión que muchos clientes tendrán de su empresa. Construimos sitios rápidos, seguros y fáciles de administrar, pensados desde el primer día para posicionarse en Google y convertir visitas en contactos.',
            'Elegimos la tecnología según su caso: Drupal para portales con muchos contenidos, roles y flujos de aprobación; WordPress para sitios corporativos y blogs que su equipo pueda editar sin ayuda técnica; React o Next.js para aplicaciones web y experiencias interactivas.',
        ],
        deliverables: {
            title: '¿Qué incluye nuestro servicio de desarrollo web?',
            items: [
                'Sitios web corporativos y landing pages orientadas a conversión.',
                'Portales institucionales y sitios con gestor de contenidos (Drupal, WordPress).',
                'Aplicaciones web (SPA/SSR) con React y Next.js conectadas a APIs.',
                'Rediseño y migración de sitios antiguos (incluida migración de Drupal 7 a Drupal 10/11).',
                'Optimización de velocidad (Core Web Vitals), seguridad y SEO técnico.',
                'Dominio, hosting, correos corporativos y mantenimiento mensual.',
            ],
        },
        process: {
            title: 'Cómo trabajamos',
            steps: [
                { title: 'Estrategia', text: 'Definimos objetivos, público, estructura de páginas y palabras clave.' },
                { title: 'Diseño UX/UI', text: 'Prototipos y diseño visual responsivo alineado con su marca.' },
                { title: 'Desarrollo', text: 'Implementación en Drupal, WordPress o React con buenas prácticas de rendimiento y accesibilidad.' },
                { title: 'Lanzamiento', text: 'Configuración de dominio, SSL, Google Search Console y analítica.' },
                { title: 'Mantenimiento', text: 'Actualizaciones, respaldos, monitoreo y mejoras continuas.' },
            ],
        },
        technologies: ['Drupal', 'WordPress', 'React', 'Next.js', 'Tailwind CSS', 'Bootstrap', 'PHP', 'Node.js'],
        benefits: [
            'Sitios administrables por su propio equipo.',
            'Diseño responsivo: se ve bien en móvil, tablet y escritorio.',
            'SEO técnico incluido: metadatos, datos estructurados, sitemap y velocidad.',
            (ctx) =>
                ctx.hasPrice('Inicial')
                    ? `Planes desde ${ctx.price('Inicial')}/mes con dominio, hosting y correos incluidos.`
                    : 'Planes mensuales con dominio, hosting y correos incluidos.',
        ],
        pricing: {
            public: true,
            model: 'subscription',
            currency: 'DOP',
            tiers: [
                {
                    name: 'Inicial',
                    from: { DOP: 3800, USD: null }, // TODO: precio USD pendiente para /latam y /us
                    period: 'mes',
                    includes: 'Página web estática, formulario de contacto, dominio www.MiEmpresa.com, alojamiento y correos @MiEmpresa.com.',
                },
                {
                    name: 'Avanzado',
                    from: { DOP: 5990, USD: null }, // TODO: precio USD pendiente
                    period: 'mes',
                    includes: 'Sitio administrable (WordPress/Drupal), diseño a la medida, dominio, alojamiento, correos y mantenimiento.',
                },
                {
                    name: 'Proyecto a la medida',
                    from: null,
                    period: null,
                    includes: 'Portales, aplicaciones web y migraciones. Se cotiza por alcance.',
                },
            ],
            setupNote: '',
        },
        faqs: [
            {
                question: '¿Cuánto cuesta una página web en República Dominicana?',
                answer: (ctx) =>
                    ctx.hasPrice('Inicial')
                        ? `Nuestros planes web inician en ${ctx.price('Inicial')} mensuales para un sitio estático con formulario de contacto, dominio, alojamiento y correos corporativos, y desde ${ctx.price('Avanzado')} mensuales para sitios administrables. Proyectos a la medida (portales, aplicaciones web) se cotizan por alcance.`
                        : 'Ofrecemos planes mensuales que incluyen dominio, alojamiento y correos corporativos, y proyectos a la medida (portales, aplicaciones web) que se cotizan por alcance. Solicite una cotización en su moneda.',
            },
            {
                question: '¿Drupal o WordPress? ¿Cuál me conviene?',
                answer:
                    'WordPress es ideal para sitios corporativos, blogs y landing pages que su equipo quiera editar fácilmente. Drupal conviene para portales grandes con múltiples tipos de contenido, permisos por roles, varios idiomas o integraciones complejas. Le asesoramos sin compromiso según su caso.',
            },
            {
                question: '¿Incluyen posicionamiento SEO?',
                answer:
                    'Todos nuestros sitios se entregan con SEO técnico base (títulos, descripciones, datos estructurados, sitemap, velocidad). Adicionalmente ofrecemos planes de SEO continuo y campañas de Google Ads.',
            },
            {
                question: '¿En cuánto tiempo estará listo mi sitio web?',
                answer: 'Una landing o sitio corporativo estándar toma de 2 a 4 semanas. Portales con gestor de contenido o aplicaciones web, de 6 a 12 semanas según el alcance.',
            },
        ],
        related: ['ecommerce-tiendas-en-linea', 'marketing-digital-seo-ads', 'desarrollo-software-a-medida'],
        homeCard: {
            title: 'Desarrollo web',
            description: 'Optimice su presencia en línea con sitios web rápidos y administrables construidos en Drupal, WordPress o React.',
        },
    },

    // ───────────────────────────────────────────────────────────────────────
    // 4. Apps móviles
    // ───────────────────────────────────────────────────────────────────────
    {
        slug: 'desarrollo-aplicaciones-moviles',
        name: 'Desarrollo de aplicaciones móviles',
        shortName: 'Apps móviles',
        icon: 'fables-iconmobileApp-icon',
        image: `${IMG}/mobile-apps.jpg`,
        markets: ['RD', 'LATAM', 'USA'],
        featured: true,
        overrides: {
            LATAM: {
                seoTitle: 'Desarrollo de aplicaciones móviles iOS y Android para Latinoamérica',
                metaDescription:
                    'Aplicaciones móviles para iOS y Android con React Native y Expo para empresas de Latinoamérica: apps para clientes, apps internas, notificaciones push, modo offline y publicación en las tiendas. Cotizaciones en USD.',
            },
            USA: {
                seoTitle: 'iOS and Android app development from the Dominican Republic (React Native)',
                metaDescription:
                    'Cross-platform mobile apps with React Native and Expo built by a nearshore team on US Eastern hours: customer apps, internal apps, push notifications, offline mode, App Store and Google Play publishing. Content in Spanish.',
            },
        },
        seoTitle: 'Desarrollo de aplicaciones móviles iOS y Android en República Dominicana',
        metaDescription:
            'Creamos aplicaciones móviles para iOS y Android con React Native y Expo: apps para clientes, apps internas para su equipo, notificaciones push, modo offline y publicación en App Store y Google Play.',
        heroTitle: 'Desarrollo de aplicaciones móviles a la medida',
        heroSubtitle: 'Apps iOS y Android con una sola base de código, publicadas en App Store y Google Play.',
        intro: [
            'Transformamos su idea en una aplicación móvil lista para sus clientes o para su equipo de trabajo. Utilizamos React Native y Expo para construir apps nativas para iOS y Android con una sola base de código, lo que reduce costos y tiempos sin sacrificar experiencia de usuario.',
            'Nos encargamos de todo el ciclo: diseño de la interfaz, desarrollo, backend y APIs, pruebas en dispositivos reales, publicación en las tiendas y actualizaciones posteriores.',
        ],
        deliverables: {
            title: '¿Qué tipo de apps desarrollamos?',
            items: [
                'Apps para clientes: pedidos, reservas, catálogos, fidelización y pagos.',
                'Apps internas: fuerza de ventas, rutas, inventario, captura de datos en campo con modo offline.',
                { text: 'Apps conectadas a su sistema, CRM o software de facturación electrónica mediante APIs.', markets: ['RD'] },
                { text: 'Apps conectadas a su sistema, CRM o software de facturación mediante APIs.', markets: ['LATAM', 'USA'] },
                'Notificaciones push, geolocalización, cámara, firma digital y autenticación segura.',
                'Publicación y mantenimiento en App Store y Google Play.',
            ],
        },
        process: {
            title: 'Cómo trabajamos',
            steps: [
                { title: 'Definición', text: 'Objetivos, usuarios, funcionalidades clave y plataforma (iOS, Android o ambas).' },
                { title: 'Diseño UX/UI', text: 'Prototipos navegables para validar la experiencia antes de programar.' },
                { title: 'Desarrollo', text: 'App con React Native/Expo + backend y APIs; builds de prueba para su equipo.' },
                { title: 'Publicación', text: 'Cuentas de desarrollador, cumplimiento de políticas de las tiendas y lanzamiento.' },
                { title: 'Evolución', text: 'Monitoreo, actualizaciones y nuevas funcionalidades.' },
            ],
        },
        technologies: ['React Native', 'Expo', 'Firebase', 'Node.js', 'PostgreSQL', 'REST APIs'],
        benefits: [
            'Una base de código para iOS y Android: menor costo y mantenimiento.',
            'Integración con sus sistemas existentes.',
            'Acompañamiento en la publicación en las tiendas.',
            'Diseño centrado en el usuario final.',
        ],
        pricing: { public: false, model: 'quote', currency: 'DOP', tiers: [], setupNote: '' },
        faqs: [
            {
                question: '¿Cuánto cuesta desarrollar una aplicación móvil?',
                answer:
                    'Depende de las funcionalidades: una app de catálogo o reservas es un proyecto de semanas; una app con pagos, modo offline y backend propio es un proyecto de varios meses. Cotizamos por alcance tras una reunión de descubrimiento sin costo.',
            },
            {
                question: '¿La app funcionará en iPhone y Android?',
                answer: 'Sí. Con React Native construimos ambas versiones desde una sola base de código, y las publicamos en App Store y Google Play.',
            },
            {
                question: '¿Pueden conectar la app con mi sistema actual?',
                answer: 'Sí. Desarrollamos las APIs necesarias para conectar la app con su ERP, CRM, sistema de facturación o base de datos existente.',
            },
        ],
        related: ['facturacion-electronica-ecf', 'desarrollo-software-a-medida', 'desarrollo-web', 'ecommerce-tiendas-en-linea'],
        homeCard: {
            title: 'Aplicaciones móviles',
            description: 'Apps iOS y Android a la medida de su negocio, con una sola base de código y publicación en las tiendas.',
        },
    },

    // ───────────────────────────────────────────────────────────────────────
    // 5. eCommerce
    // ───────────────────────────────────────────────────────────────────────
    {
        slug: 'ecommerce-tiendas-en-linea',
        name: 'Desarrollo de eCommerce y tiendas en línea',
        shortName: 'eCommerce',
        icon: 'fables-iconcart',
        image: `${IMG}/ecommerce.jpg`, // TODO: crear imagen (antes compartía laptop-1209008.jpg)
        markets: ['RD', 'LATAM', 'USA'],
        featured: true,
        overrides: {
            LATAM: {
                seoTitle: 'Desarrollo de tiendas en línea (eCommerce) para empresas de Latinoamérica',
                metaDescription:
                    'Creamos tiendas en línea con pasarela de pago, inventario, envíos y facturación integrada. eCommerce con WooCommerce, Drupal Commerce o desarrollo a la medida para empresas de Latinoamérica. Cotizaciones en USD.',
            },
            USA: {
                seoTitle: 'eCommerce development from the Dominican Republic: WooCommerce, Drupal Commerce, Next.js',
                metaDescription:
                    'Online stores with payment gateways (Stripe, PayPal), inventory, shipping and invoicing integrations, built by a nearshore team on US Eastern hours. Content in Spanish; contact us in English.',
            },
        },
        seoTitle: 'Desarrollo de tiendas en línea (eCommerce) en República Dominicana',
        metaDescription:
            'Creamos tiendas en línea con pasarela de pago, inventario, envíos y facturación electrónica e-CF integrada. eCommerce con WooCommerce, Drupal Commerce o desarrollo a la medida en Santo Domingo.',
        heroTitle: 'Eleve sus ventas con una tienda en línea',
        heroSubtitle: 'eCommerce con pagos en línea, control de inventario y facturación integrada.',
        intro: [
            'Vender en internet ya no es opcional. Diseñamos y desarrollamos tiendas en línea que convierten: catálogo claro, proceso de compra corto, pagos con tarjeta o transferencia y logística de envíos integrada.',
            'A diferencia de las plantillas genéricas, nuestras tiendas se conectan con lo que su negocio ya usa: inventario, punto de venta, contabilidad y facturación, para que cada venta en línea quede registrada automáticamente.',
        ],
        deliverables: {
            title: '¿Qué incluye?',
            items: [
                'Tienda en línea con WooCommerce, Drupal Commerce o desarrollo a la medida (React/Next.js).',
                'Integración con pasarelas de pago locales e internacionales (tarjetas, transferencia, PayPal, Stripe).',
                'Gestión de productos, variantes, inventario, cupones y envíos.',
                { text: 'Facturación electrónica e-CF integrada en cada compra.', markets: ['RD'] },
                { text: 'Dashboard de ventas y sincronización con POS/CRM.' },
                { text: 'SEO para eCommerce y campañas de Google/Facebook Ads.' },
            ],
        },
        process: {
            title: 'Cómo trabajamos',
            steps: [
                { title: 'Modelo de negocio', text: 'Catálogo, medios de pago, envíos y políticas.' },
                { title: 'Diseño', text: 'Experiencia de compra optimizada para móvil.' },
                { title: 'Desarrollo e integraciones', text: 'Pasarela de pago, inventario, facturación y logística.' },
                { title: 'Lanzamiento y crecimiento', text: 'Analítica, SEO y campañas para atraer compradores.' },
            ],
        },
        technologies: ['WooCommerce', 'Drupal Commerce', 'Next.js', 'Stripe', 'PayPal', 'PostgreSQL', 'MySQL'],
        benefits: [
            'Ventas 24/7 con pagos en línea seguros.',
            { text: 'Facturación electrónica e-CF automática por cada pedido.', markets: ['RD'] },
            'Un solo inventario para tienda física y en línea.',
            'Estrategia de tráfico incluida (SEO y Ads).',
        ],
        pricing: { public: false, model: 'project', currency: 'DOP', tiers: [], setupNote: '' },
        faqs: [
            {
                question: '¿Puedo aceptar tarjetas de crédito en mi tienda en línea en República Dominicana?',
                answer: 'Sí. Integramos pasarelas de pago locales (bancos y procesadores dominicanos) e internacionales como PayPal o Stripe, además de pago por transferencia.',
                markets: ['RD'],
            },
            {
                question: '¿Qué medios de pago puedo aceptar en mi tienda en línea?',
                answer: 'Tarjetas de crédito y débito mediante Stripe, PayPal u otras pasarelas de su país, además de transferencias y pagos contra entrega si su operación lo requiere.',
                markets: ['LATAM', 'USA'],
            },
            {
                question: '¿La tienda emite facturas electrónicas (e-CF)?',
                answer:
                    'Sí. Coralan Multiservice está certificada como Emisor Electrónico ante la DGII y hemos acompañado a 5 empresas en su propia certificación. Podemos integrar la emisión de comprobantes fiscales electrónicos válidos ante la DGII en cada venta de su tienda.',
                markets: ['RD'],
            },
            {
                question: '¿Puedo administrar la tienda yo mismo?',
                answer: 'Sí. Le capacitamos para gestionar productos, precios, inventario y pedidos desde un panel sencillo, sin conocimientos técnicos.',
            },
        ],
        related: ['facturacion-electronica-ecf', 'desarrollo-web', 'marketing-digital-seo-ads', 'desarrollo-software-a-medida'],
        homeCard: {
            title: 'eCommerce y tiendas en línea',
            description: 'Expanda su negocio y aumente sus ingresos con una tienda en línea con pagos, inventario y facturación integrados.',
        },
    },

    // ───────────────────────────────────────────────────────────────────────
    // 6. Automatizaciones con IA
    // ───────────────────────────────────────────────────────────────────────
    {
        slug: 'automatizaciones-inteligencia-artificial',
        name: 'Automatizaciones con inteligencia artificial',
        shortName: 'Automatización con IA',
        icon: 'fables-iconlamp-icon',
        image: `${IMG}/lamp.jpg`,
        markets: ['RD', 'LATAM', 'USA'],
        featured: true,
        overrides: {
            USA: {
                seoTitle: 'AI process automation for businesses (nearshore team, Dominican Republic)',
                metaDescription:
                    'We automate repetitive processes with AI: WhatsApp and web assistants, document classification, invoice data extraction, automatic reports and system integrations. Content in Spanish; contact us in English.',
            },
        },
        seoTitle: 'Automatización de procesos con inteligencia artificial para empresas',
        metaDescription:
            'Automatizamos procesos repetitivos con IA: asistentes virtuales para WhatsApp y web, clasificación de documentos, extracción de datos de facturas, reportes automáticos e integraciones entre sistemas.',
        heroTitle: 'Automatice procesos con inteligencia artificial',
        heroSubtitle: 'Menos tareas manuales, respuestas más rápidas y datos siempre al día.',
        intro: [
            'La inteligencia artificial dejó de ser un lujo para grandes corporaciones. Ayudamos a empresas a automatizar tareas repetitivas que hoy consumen horas de su equipo: responder consultas frecuentes, digitar facturas, clasificar correos, generar reportes o mover datos entre sistemas.',
            'Combinamos modelos de lenguaje (LLM) con integraciones a sus herramientas actuales para crear flujos que trabajan solos, con supervisión humana donde importa.',
        ],
        deliverables: {
            title: '¿Qué automatizamos?',
            items: [
                'Asistentes virtuales (chatbots) para WhatsApp, web y redes que responden con la información de su negocio.',
                'Extracción de datos de facturas, PDFs y correos hacia su sistema contable o CRM.',
                'Clasificación y enrutamiento automático de solicitudes, tickets y correos.',
                'Generación de reportes, resúmenes y alertas periódicas.',
                'Integraciones entre aplicaciones (formularios, hojas de cálculo, CRM, facturación, mensajería).',
                'Asistentes internos que consultan la documentación y datos de la empresa.',
            ],
        },
        process: {
            title: 'Cómo trabajamos',
            steps: [
                { title: 'Diagnóstico', text: 'Identificamos los procesos con mayor tiempo manual y mejor retorno.' },
                { title: 'Prueba piloto', text: 'Automatizamos un proceso concreto en pocas semanas y medimos resultados.' },
                { title: 'Escalado', text: 'Extendemos la automatización a más áreas e integramos con sus sistemas.' },
                { title: 'Monitoreo', text: 'Supervisión, ajustes y mejoras continuas.' },
            ],
        },
        technologies: ['OpenAI / Anthropic', 'Node.js', 'Python', 'WhatsApp Business API', 'Firebase', 'Google Cloud', 'AWS'],
        benefits: [
            'Reducción de horas en tareas repetitivas.',
            'Atención al cliente 24/7 con respuestas consistentes.',
            'Menos errores de digitación y datos centralizados.',
            'Implementación por fases con resultados medibles.',
        ],
        pricing: { public: false, model: 'quote', currency: 'DOP', tiers: [], setupNote: '' },
        faqs: [
            {
                question: '¿Qué procesos de mi empresa se pueden automatizar con IA?',
                answer: 'Los mejores candidatos son tareas repetitivas basadas en texto o documentos: responder preguntas frecuentes, digitar facturas o formularios, clasificar solicitudes, generar reportes y sincronizar datos entre sistemas. En el diagnóstico inicial priorizamos por impacto y facilidad.',
            },
            {
                question: '¿Necesito cambiar mis sistemas actuales?',
                answer: 'No. Las automatizaciones se integran con las herramientas que ya usa (correo, WhatsApp, hojas de cálculo, CRM, software contable) mediante APIs o conectores.',
            },
            {
                question: '¿Es seguro usar IA con los datos de mi empresa?',
                answer: 'Diseñamos cada solución definiendo qué datos se procesan, dónde se almacenan y qué proveedores se utilizan, con controles de acceso y sin compartir información sensible innecesariamente.',
            },
        ],
        related: ['desarrollo-software-a-medida', 'marketing-digital-seo-ads', 'desarrollo-web'],
        homeCard: {
            title: 'Automatizaciones con IA',
            description: 'Automatice procesos repetitivos e impulse la productividad de su empresa con inteligencia artificial.',
        },
    },

    // ───────────────────────────────────────────────────────────────────────
    // 7. Marketing digital (RD y LATAM)
    // ───────────────────────────────────────────────────────────────────────
    {
        slug: 'marketing-digital-seo-ads',
        name: 'Marketing digital: SEO, Google Ads, Facebook Ads y mail marketing',
        shortName: 'Marketing digital',
        icon: 'fables-iconeye',
        image: `${IMG}/marketing-digital.jpg`, // TODO: crear imagen (antes usaba index-background.jpg, la del hero)
        markets: ['RD', 'LATAM'],
        featured: true,
        overrides: {
            LATAM: {
                seoTitle: 'Agencia de marketing digital para Latinoamérica: SEO, Google Ads y Facebook Ads',
                metaDescription:
                    'Posicionamiento SEO, campañas de Google Ads y Facebook/Instagram Ads, mail marketing y diseño para redes sociales para empresas de Latinoamérica. Más clientes potenciales optimizando su presupuesto.',
            },
        },
        seoTitle: 'Agencia de marketing digital en Santo Domingo: SEO, Google Ads y Facebook Ads',
        metaDescription:
            'Posicionamiento SEO, campañas de Google Ads y Facebook/Instagram Ads, mail marketing y diseño para redes sociales en República Dominicana. Más clientes potenciales optimizando su presupuesto.',
        heroTitle: 'Marketing digital que genera clientes',
        heroSubtitle: 'SEO, Google Ads, Facebook e Instagram Ads, mail marketing y diseño para su marca.',
        intro: [
            'Tener un sitio web no sirve de nada si nadie lo encuentra. Combinamos posicionamiento orgánico (SEO), publicidad pagada (Google Ads y Meta Ads) y mail marketing para que su empresa aparezca cuando sus clientes buscan lo que usted ofrece.',
            'Somos desarrolladores además de marketers: aplicamos SEO técnico real sobre su sitio, medimos conversiones de punta a punta y optimizamos su presupuesto para obtener más clientes potenciales, no solo clics.',
        ],
        deliverables: {
            title: '¿Qué incluye?',
            items: [
                'Auditoría y optimización SEO técnica y de contenidos.',
                'Estrategia de palabras clave y creación de páginas/artículos orientados a búsqueda.',
                'Campañas de Google Ads (búsqueda, display, remarketing) y Facebook/Instagram Ads.',
                'Mail marketing: campañas, automatizaciones y segmentación de audiencias.',
                'Diseño gráfico para web, redes sociales y anuncios.',
                'Google Business Profile, analítica y reportes mensuales.',
            ],
        },
        process: {
            title: 'Cómo trabajamos',
            steps: [
                { title: 'Auditoría', text: 'Analizamos su sitio, competencia y palabras clave.' },
                { title: 'Plan', text: 'Definimos objetivos, canales, presupuesto y calendario.' },
                { title: 'Ejecución', text: 'Optimizaciones SEO, campañas y contenidos.' },
                { title: 'Medición', text: 'Reportes mensuales de tráfico, leads y retorno.' },
            ],
        },
        technologies: ['Google Search Console', 'Google Analytics 4', 'Google Ads', 'Meta Ads', 'Mailchimp', 'Google Business Profile'],
        benefits: [
            'Más visibilidad en Google y en asistentes de IA.',
            'Presupuesto publicitario optimizado por conversiones.',
            'Reportes claros de resultados.',
            'Equipo técnico que puede implementar los cambios en su sitio.',
        ],
        pricing: { public: false, model: 'subscription', currency: 'DOP', tiers: [], setupNote: '' },
        faqs: [
            {
                question: '¿Cuánto tarda el SEO en dar resultados?',
                answer: 'Los cambios técnicos se reflejan en semanas; el posicionamiento orgánico competitivo suele consolidarse entre 3 y 6 meses de trabajo constante. Mientras tanto, las campañas de Google Ads generan resultados inmediatos.',
            },
            {
                question: '¿Cuánto debo invertir en Google Ads o Facebook Ads?',
                answer: 'Depende de su sector y competencia. Definimos un presupuesto inicial de prueba, medimos el costo por cliente potencial y ajustamos la inversión hacia lo que mejor funciona.',
            },
            {
                question: '¿Trabajan con empresas fuera de Santo Domingo?',
                answer: 'Sí. Atendemos empresas en toda República Dominicana y clientes de Latinoamérica de forma remota.',
            },
        ],
        related: ['desarrollo-web', 'ecommerce-tiendas-en-linea', 'automatizaciones-inteligencia-artificial'],
        homeCard: {
            title: 'Marketing digital',
            description: 'Posicionamiento SEO, campañas en Google y Facebook Ads y mail marketing para conseguir más clientes potenciales.',
        },
    },

    // ───────────────────────────────────────────────────────────────────────
    // 8. Nearshore development teams (USA / LATAM / RD) — contenido en inglés
    // ───────────────────────────────────────────────────────────────────────
    {
        slug: 'equipos-desarrollo-nearshore',
        name: 'Nearshore software development teams',
        shortName: 'Nearshore teams',
        icon: 'fables-iconbussiness2',
        image: `${IMG}/nearshore-teams.jpg`, // TODO: crear imagen
        markets: ['USA', 'LATAM', 'RD'],
        featured: true,
        lang: 'en',
        seoTitle: 'Nearshore Software Development Teams in the Dominican Republic (US Eastern Time)',
        metaDescription:
            'Dedicated nearshore developers and staff augmentation from the Dominican Republic: React, Next.js, Node.js, Laravel, React Native, QA and DevOps. Full overlap with US Eastern business hours, English and Spanish, you own the code.',
        heroTitle: 'Nearshore development teams that work your hours',
        heroSubtitle:
            'The Dominican Republic runs on Atlantic Time: your team is online during the whole US East Coast workday, not the next day. Senior developers, English and Spanish, no intermediaries.',
        intro: [
            'Offshore vendors in India or Eastern Europe hand you work while you sleep and answer questions the next morning. Our engineers in Santo Domingo work the same hours as your team in New York, Miami or Boston, join your stand-ups live and ship inside your workflow (Jira, GitHub, Slack).',
            'Coralan Multiservice has been building software for more than 15 years for companies in the Dominican Republic and abroad. You get a dedicated team, individual engineers to extend your own team, or a fixed-scope project — always with clear ownership of the code and the data.',
        ],
        deliverables: {
            title: 'Engagement models and roles',
            items: [
                'Dedicated team: a stable, cross-functional squad managed with you (PM/tech lead, developers, QA).',
                'Staff augmentation: individual engineers embedded in your team, reporting to your leads.',
                'Fixed-scope projects: defined deliverables, timeline and price.',
                'Front-end: React, Next.js, TypeScript, Tailwind CSS.',
                'Back-end: Node.js, PHP / Laravel, REST APIs, PostgreSQL, MySQL, Firebase.',
                'Mobile: React Native / Expo for iOS and Android.',
                'QA and DevOps: automated testing, CI/CD, AWS and Google Cloud.',
            ],
        },
        process: {
            title: 'How we start',
            steps: [
                { title: 'Discovery call', text: 'Stack, roles, seniority, time zone needs and expected start date.' },
                { title: 'Team proposal', text: 'Profiles, monthly rate per role and a simple agreement (NDA and IP assignment included).' },
                { title: 'Onboarding', text: 'Access, tools and rituals set up in your environment during the first week.' },
                { title: 'Delivery', text: 'Sprints in your workflow, live stand-ups in EST, transparent reporting.' },
                { title: 'Scale up or down', text: 'Add or reduce engineers with 30 days notice, no hiring costs.' },
            ],
        },
        technologies: ['React', 'Next.js', 'TypeScript', 'Node.js', 'PHP / Laravel', 'React Native', 'PostgreSQL', 'AWS', 'Google Cloud'],
        benefits: [
            'Full overlap with US Eastern Time business hours (Atlantic Standard Time, no DST).',
            'Communication in English and Spanish, directly with the engineers — no intermediaries.',
            'Lower cost than hiring in the US, with senior engineers.',
            'No payroll, recruiting or hiring costs on your side.',
        ],
        pricing: { public: false, model: 'quote', currency: 'USD', tiers: [], setupNote: '' },
        faqs: [
            {
                question: 'Who owns the intellectual property and the source code?',
                answer:
                    'You do. Every agreement includes IP assignment: all code, designs and documentation produced for you belong to your company from the moment they are created. Work happens in your repositories and cloud accounts whenever possible, and we sign an NDA before any discovery.',
            },
            {
                question: 'What time zone does the team work in?',
                answer:
                    'Atlantic Standard Time (UTC−4) all year. That is the same as US Eastern Daylight Time in summer and one hour ahead of Eastern Standard Time in winter, so your team overlaps the entire East Coast workday.',
            },
            {
                question: 'How fast can a developer or a team start?',
                answer:
                    'Individual engineers typically start within 1–3 weeks; a full dedicated team within 3–5 weeks depending on the roles. We agree on a trial period so you can evaluate the fit with no long-term commitment.',
            },
            {
                question: 'How do you handle communication and reporting?',
                answer:
                    'Our engineers join your daily stand-ups and use your tools (Slack, Teams, Jira, GitHub, Linear). You get weekly progress reports and a single point of contact for staffing changes.',
            },
            {
                question: '¿Ofrecen equipos nearshore para empresas de Latinoamérica?',
                answer:
                    'Sí. Trabajamos con empresas de habla hispana en toda la región en su mismo huso horario o con solapamiento completo, con equipos dedicados o desarrolladores individuales que se integran a su equipo.',
                markets: ['LATAM', 'RD'],
            },
        ],
        related: ['desarrollo-software-a-medida', 'desarrollo-web', 'desarrollo-aplicaciones-moviles'],
        homeCard: {
            title: 'Equipos nearshore',
            description: 'Desarrolladores dedicados en horario de la costa este de EE. UU., en inglés y español, para empresas de EE. UU. y Latinoamérica.',
        },
    },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

export const getService = (slug) => SERVICES.find((s) => s.slug === slug);
export const servicePath = (slug) => `/servicios/${slug}`;

export const servicesByMarket = (market = DEFAULT_MARKET) =>
    SERVICES.filter((s) => (s.markets ?? ['RD']).includes(market));

export const featuredServices = (market = DEFAULT_MARKET) =>
    servicesByMarket(market).filter((s) => s.featured);

export const servicePathFor = (slug, market = DEFAULT_MARKET) =>
    `${MARKETS[market].pathPrefix}/servicios/${slug}`;

export const servicesIndexPathFor = (market = DEFAULT_MARKET) => `${MARKETS[market].pathPrefix}/servicios`;

/** Servicio con los textos del mercado aplicados (overrides[market] sobre los de RD). */
export const serviceForMarket = (service, market = DEFAULT_MARKET) =>
    service && service.overrides && service.overrides[market] ? { ...service, ...service.overrides[market] } : service;

/** Rutas de servicios a prerenderizar y a incluir en el sitemap (todos los mercados). */
export const allServiceRoutes = () =>
    Object.keys(MARKETS).flatMap((m) => servicesByMarket(m).map((s) => servicePathFor(s.slug, m)));

/** Rutas de índice /servicios por mercado. */
export const allServiceIndexRoutes = () => Object.keys(MARKETS).map((m) => servicesIndexPathFor(m));

/**
 * Contexto de precios para un servicio en un mercado: qué moneda mostrar y helpers
 * `price(tierName)` / `hasPrice(tierName)` para textos dinámicos.
 * Regla: la facturación electrónica es siempre DOP; el resto usa la moneda del mercado.
 */
export const pricingContext = (service, market = DEFAULT_MARKET) => {
    const mk = MARKETS[market] || MARKETS[DEFAULT_MARKET];
    const pricing = service.pricing || null;
    const forceBase = service.slug === 'facturacion-electronica-ecf';
    const currency = forceBase && pricing ? pricing.currency : mk.currency;
    const locale = currency === 'DOP' ? 'es-DO' : mk.locale;
    const tierAmount = (name) => {
        const tier = pricing?.tiers?.find((t) => t.name === name);
        return tier ? resolveAmount(tier.from, currency, pricing.currency) : null;
    };
    return {
        market,
        currency,
        currencySymbol: currency === 'DOP' ? 'RD$' : mk.currencySymbol,
        locale,
        hasPrice: (name) => tierAmount(name) != null,
        price: (name) => {
            const amount = tierAmount(name);
            return amount == null ? null : formatPrice(amount, currency, locale);
        },
        tiers: (pricing?.tiers || []).map((t) => {
            const amount = resolveAmount(t.from, currency, pricing.currency);
            return { ...t, amount, formatted: amount == null ? null : formatPrice(amount, currency, locale) };
        }),
    };
};

const resolveText = (text, ctx) => (typeof text === 'function' ? text(ctx) : text);

/** string | fn | { text, markets? }  →  string[] filtrados por mercado. */
export const resolveList = (items = [], market = DEFAULT_MARKET, service = null) => {
    const ctx = service ? pricingContext(service, market) : { market };
    return items
        .filter((i) => !(i && typeof i === 'object' && i.markets) || i.markets.includes(market))
        .map((i) => (i && typeof i === 'object' && !Array.isArray(i) ? resolveText(i.text, ctx) : resolveText(i, ctx)))
        .filter(Boolean);
};

/** FAQs del servicio para un mercado, con respuestas ya resueltas a string. */
export const resolveFaqs = (service, market = DEFAULT_MARKET) => {
    const ctx = pricingContext(service, market);
    return (service.faqs || [])
        .filter((f) => !f.markets || f.markets.includes(market))
        .map((f) => ({ question: resolveText(f.question, ctx), answer: resolveText(f.answer, ctx) }));
};
